import {
  createContext, useContext, useEffect, useRef, useState, type ReactNode,
} from 'react'
import { Search, Menu } from 'lucide-react'
import { usePath, navigate, isAuthPath, navigationKind, navigationIndex, useNavigationCount } from '../router.js'
import { EVT_ASSOCIATIONS } from '../associations/associations-model.js'
import { usePrefsStore, applyPrefs, setPageTitle, syncFromServer } from '../prefs.js'
import { cheminDe, titleForPath } from '../navigation/site-navigation.js'
import { useAdresseCourante } from '../navigation/use-adresse-courante.js'
import { Sidebar } from './Sidebar.js'
import { CommandPalette } from './CommandPalette.js'
import { ToastProvider } from '../admin/primitives.js'
import { EditingProvider } from './editing.js'
import { ConfirmProvider } from './confirm.js'
import { MenuContextuelProvider } from './menu-contextuel.js'
import { enterApp } from './auth-motion.js'
import { computeGlobalStatus, type GlobalStatus, type StatusCardInput } from './status.js'
import { onUnauthenticated, markAuthenticated, wrongOrigin } from './auth-routing.js'
import { SessionExpiryGuard } from './session-expiry.js'
import { SceauHost } from './SceauHost.js'
import { AVoir } from './AVoir.js'
import { SecureOriginPage } from '../auth/InfoPages.js'
import { BoutonFavori } from './BoutonFavori.js'
import { ModuleOnglets } from '../navigation/ModuleOnglets.js'
import { chargerFavoris, viderFavoris } from './favoris.js'
import { ActualisationsHote } from '../supervision/vues/actualiser.js'

/**
 * PERSISTENT APP SHELL (LOT 5.5).
 *
 * Monté UNE FOIS : sidebar, header, palette, profil vivent en continu —
 * seule la zone de contenu change à la navigation. C'est la correction de
 * fond des saccades : plus aucun rechargement de document, plus aucun
 * remontage du shell, plus aucun refetch des métadonnées à chaque clic.
 *
 * La transition de route est une simple APPARITION du nouveau contenu
 * (180 ms, fondu + 5 px) : aucun calque de sortie, donc aucun overlay
 * fantôme possible — les clics ne sont jamais bloqués.
 */

export interface ShellMeta {
  tempsBadge: number
  /** « N à décider » des Associations — pour qui peut décider (`association.manage`), 0 sinon. */
  associationsBadge?: number
  servicesFresh: number | null
  servicesTotal: number | null
  alertsBadge: number
  /** Global Status (LOT 5.5.3) — null tant que le premier fetch n'a pas
   *  abouti. Optionnel uniquement pour le shim de compatibilité LOT 4/5
   *  (admin/Shell.tsx), qui ignore meta ; le shell réel le fournit toujours. */
  status?: GlobalStatus | null
}

export interface MeInfo {
  id: string
  display_name: string
  first_name: string | null
  last_name: string | null
  email: string
  phone: string | null
  avatar: string | null
  role_label: string
  roles: string[]
  permissions: string[]
  mfa_enabled: boolean
  passkeys: number
  preferred_mfa: string | null
  recovery_codes_remaining: number
}

const MetaCtx = createContext<ShellMeta>({ servicesFresh: null, servicesTotal: null, alertsBadge: 0, tempsBadge: 0, status: null })
const MeCtx = createContext<MeInfo | null>(null)
const MeReloadCtx = createContext<() => void>(() => undefined)
export const useShellMetaCtx = () => useContext(MetaCtx)
export const useMe = () => useContext(MeCtx)
/** Recharge /api/v1/me dans tout le shell (après une modification du profil). */
export const useMeReload = () => useContext(MeReloadCtx)

const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super Admin', direction: 'Direction', securite: 'Sécurité',
  technique: 'Technique', lecture_seule: 'Lecture seule',
}

/* IA V2 — les titres viennent du registre central ; seuls les écrans hors
   taxonomie (zone compte) gardent une entrée locale. */
const EXTRA_TITLES: Record<string, string> = { '/profile': 'Mon profil', '/moi': 'Mon espace' }

interface DashboardMeta {
  services_total: number; services_fresh: number
  alerts_active_unacked: number
  alerts_unacked_buckets?: { all: number; action: number; critical: number }
  cards?: StatusCardInput[]
  worker?: { active: boolean; seconds_since_seen: number } | null
}

export function AppShell(props: { children: ReactNode }) {
  const path = usePath()
  const navigations = useNavigationCount()
  const adresse = useAdresseCourante(path)
  const prefs = usePrefsStore()
  const [dash, setDash] = useState<DashboardMeta | null>(null)
  const [me, setMe] = useState<MeInfo | null>(null)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [mobileNav, setMobileNav] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  /* Métadonnées d'en-tête : UNE source, rafraîchie à rythme calme —
     plus de refetch à chaque navigation. */
  const [offline, setOffline] = useState(false)
  useEffect(() => {
    let vivant = true
    const charger = () => {
      fetch('/api/v1/dashboard', { credentials: 'same-origin' })
        .then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.json() })
        .then((j: DashboardMeta) => { if (vivant) { setDash(j); setOffline(false) } })
        // Échec = l'API Gestion elle-même ne répond plus : le Global Status
        // passe « Indisponible » en GARDANT les dernières données connues.
        .catch(() => { if (vivant) setOffline(true) })
    }
    charger()
    const t = setInterval(charger, 60_000)
    return () => { vivant = false; clearInterval(t) }
  }, [])

  const [meVersion, setMeVersion] = useState(0)
  const [origineBloquee, setOrigineBloquee] = useState<string | null>(null)
  const reloadMe = () => setMeVersion((v) => v + 1)
  useEffect(() => {
    let vivant = true
    fetch('/api/v1/me', { credentials: 'same-origin' })
      .then((r) => r.json())
      .then((j: {
        user: { id: string; display_name: string; first_name: string | null; last_name: string | null; email: string; phone: string | null; avatar: string | null } | null
        roles: string[]; permissions: string[]; mfa_enabled: boolean; passkeys?: number
        preferred_mfa?: string | null; recovery_codes_remaining: number
        enforcement?: boolean; public_origin?: string | null
      }) => {
        if (!vivant) return
        // LOT 6 §102 : origine non canonique → le portail ne se rend pas,
        // il indique le bon chemin (page « Connexion sécurisée requise »).
        if (wrongOrigin(j.public_origin)) { setOrigineBloquee(j.public_origin ?? null); enterApp(); return }
        // LOT 6 : sous enforcement, un anonyme n'a rien à faire ici.
        if (!j.user) {
          // L'entrée ne se joue PAS avant d'être sûr que l'utilisateur peut
          // entrer (§12) : sous enforcement il part vers /login, et il n'y a
          // rien à révéler ici.
          if (j.enforcement) onUnauthenticated(); else enterApp()
          return
        }
        markAuthenticated()
        enterApp()
        setMe({
          id: j.user.id, display_name: j.user.display_name,
          first_name: j.user.first_name ?? null, last_name: j.user.last_name ?? null,
          email: j.user.email, phone: j.user.phone ?? null, avatar: j.user.avatar ?? null,
          roles: j.roles, permissions: j.permissions,
          role_label: j.roles.map((r) => ROLE_LABELS[r] ?? r).join(' · ') || 'Aucun rôle',
          mfa_enabled: j.mfa_enabled, passkeys: j.passkeys ?? 0,
          preferred_mfa: j.preferred_mfa ?? null,
          recovery_codes_remaining: j.recovery_codes_remaining,
        })
      })
      // L'API ne répond pas : on révèle quand même. Une transition d'entrée
      // ne doit jamais devenir un rideau devant une panne.
      .catch(() => { enterApp() })
    if (meVersion === 0) void syncFromServer()
    return () => { vivant = false }
  }, [meVersion])

  /* Les favoris du compte (24/10/2026) : en mémoire seulement, rechargés
     quand le compte change, vidés sans session. */
  useEffect(() => {
    if (me) chargerFavoris(me.id)
    else viderFavoris()
  }, [me?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  /* LOT 6 §85 — back/forward cache : une page privée restaurée depuis le
     bfcache après déconnexion re-vérifie la session ; morte → /login.
     (Les réponses API sont no-store, ceci couvre le DOM restauré.) */
  useEffect(() => {
    const onShow = (e: PageTransitionEvent) => {
      if (!e.persisted) return
      fetch('/api/v1/auth/session', { credentials: 'same-origin' })
        .then((r) => r.json())
        .then((j: { authenticated?: boolean; enforcement?: boolean }) => {
          if (!j.authenticated && j.enforcement) onUnauthenticated()
        })
        .catch(() => undefined)
    }
    window.addEventListener('pageshow', onShow)
    return () => window.removeEventListener('pageshow', onShow)
  }, [])

  /* À chaque navigation : titre, scroll en haut, et ré-application des
     préférences (reprend la main sur les overrides locaux du panneau démo). */
  /* V2.1 — restauration de scroll (§29) : le conteneur défilant est
     .lc-scroll (pas la fenêtre), le navigateur ne peut donc pas restaurer
     seul. On mémorise la position par INDEX d'historique au fil du
     défilement ; un PUSH repart en haut, un POP retrouve sa position. */
  const scrollMemo = useRef<Map<number, number>>(new Map())
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const onScroll = () => { scrollMemo.current.set(navigationIndex(), el.scrollTop) }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [])
  /* Le TITRE suit l'adresse complète, paramètres compris : une entrée du menu
     peut désigner une vue (`?vue=ecarts`), que la page change sur place
     (`remplacerRecherche`) ou par le menu sans changer de chemin. Sur le
     chemin seul, l'onglet restait « Clôtures & caisses » sur la vue Écarts
     (revue du 07/10/2026). Un écran qui pose son propre titre (l'assistant
     de clôture) le repose APRÈS, sur la même adresse. */
  useEffect(() => {
    const chemin = cheminDe(adresse)
    setPageTitle(chemin === '/' ? null : EXTRA_TITLES[chemin] ?? titleForPath(adresse))
  }, [adresse])
  /* Défilement et préférences : à chaque NAVIGATION (empilée, remplacée,
     Retour), même vers le chemin déjà affiché — jamais sur une simple
     réécriture de paramètres. */
  useEffect(() => {
    if (navigationKind() === 'pop') {
      const memo = scrollMemo.current.get(navigationIndex()) ?? 0
      // Après le rendu de la page restaurée. Son contenu arrive souvent en
      // ASYNCHRONE : tant que la page n'est pas assez haute, poser scrollTop
      // serait écrêté à 0 et la position perdue — on réessaie jusqu'à ~2 s,
      // et on s'arrête dès que la position tient ou que la main est reprise.
      const debut = performance.now()
      let repris = false
      const reprise = () => { repris = true }
      window.addEventListener('wheel', reprise, { passive: true })
      window.addEventListener('touchmove', reprise, { passive: true })
      const nettoie = () => { window.removeEventListener('wheel', reprise); window.removeEventListener('touchmove', reprise) }
      const essaie = () => {
        const el = scrollRef.current
        if (!el || repris) return nettoie()
        if (el.scrollHeight - el.clientHeight >= memo) { el.scrollTo({ top: memo }); return nettoie() }
        if (performance.now() - debut < 2000) requestAnimationFrame(essaie)
        else nettoie()
      }
      requestAnimationFrame(() => requestAnimationFrame(essaie))
    } else if (navigationKind() === 'push') {
      scrollRef.current?.scrollTo({ top: 0 })
    }
    applyPrefs(prefs.resolved)
  }, [navigations]) // eslint-disable-line react-hooks/exhaustive-deps

  /* Interception GLOBALE des liens internes : les pages des lots gelés
     utilisent des <a href="/…"> — sans réécrire ces écrans, tout lien
     interne devient une navigation cliente (les liens externes, /api et
     target=_blank passent normalement). */
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
      const a = (e.target as HTMLElement).closest?.('a')
      if (!a || a.target === '_blank' || a.hasAttribute('download')) return
      const href = a.getAttribute('href')
      if (!href || !href.startsWith('/') || href.startsWith('/api/')) return
      e.preventDefault()
      if (isAuthPath(href)) { window.location.assign(href); return }
      navigate(href)
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [])

  /* Ctrl/Cmd+K — le raccourci universel de la palette. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen((o) => !o)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Badge MODULE Temps (doctrine Lot 2.6) : uniquement des ACTIONS MÉTIER
  // canoniques (journées à valider, congés, anomalies) — jamais un compteur
  // provider, jamais dupliqué sur la famille Équipe, rien si zéro.
  const [tempsBadge, setTempsBadge] = useState(0)
  useEffect(() => {
    let stop = false
    const load = () => {
      void fetch('/api/v1/hr/time/action-center', { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.json() : null))
        .then((j: { total?: number } | null) => { if (!stop && j) setTempsBadge(j.total ?? 0) })
        .catch(() => undefined)
    }
    load()
    const t = setInterval(load, 120_000)
    return () => { stop = true; clearInterval(t) }
  }, [])

  // Badge MODULE Associations (20/10/2026, même doctrine) : « N à décider »,
  // une action métier — suggestions à confirmer, soirées à identifier, nuits
  // sans nom. Seulement pour qui peut décider ; relu toutes les 2 minutes et
  // après chaque geste (`EVT_ASSOCIATIONS`), jamais affiché à zéro.
  const peutDeciderAssos = me?.permissions.includes('association.manage') ?? false
  const [associationsBadge, setAssociationsBadge] = useState(0)
  useEffect(() => {
    if (!peutDeciderAssos) { setAssociationsBadge(0); return }
    let stop = false
    const load = () => {
      void fetch('/api/v1/associations/resume', { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.json() : null))
        .then((j: { a_decider?: number } | null) => { if (!stop && j) setAssociationsBadge(j.a_decider ?? 0) })
        .catch(() => undefined)
    }
    load()
    const t = setInterval(load, 120_000)
    window.addEventListener(EVT_ASSOCIATIONS, load)
    return () => { stop = true; clearInterval(t); window.removeEventListener(EVT_ASSOCIATIONS, load) }
  }, [peutDeciderAssos])

  const buckets = dash?.alerts_unacked_buckets
  const badgePref = prefs.resolved['alerts_badge'] ?? 'all'
  const alertsBadge = buckets
    ? (badgePref === 'critical' ? buckets.critical : badgePref === 'action' ? buckets.action : buckets.all)
    : (dash?.alerts_active_unacked ?? 0)

  const meta: ShellMeta = {
    servicesFresh: dash?.services_fresh ?? null,
    servicesTotal: dash?.services_total ?? null,
    alertsBadge,
    tempsBadge,
    associationsBadge,
    status: dash
      ? computeGlobalStatus({
          cards: dash.cards ?? [],
          worker: dash.worker ?? null,
          alerts: buckets?.action ?? dash.alerts_active_unacked,
          offline,
          infra: (dash as { infrastructure?: import('./status.js').InfraSummaryInput }).infrastructure ?? null,
        })
      : (offline ? computeGlobalStatus({ cards: [], worker: null, alerts: 0, offline: true }) : null),
  }

  // Même règle que le menu : sans session (enforcement off), tout est visible.
  const peut = (perm?: string) => !perm || me === null || me.permissions.includes(perm)

  // Origine non canonique : rien du portail ne se monte — uniquement la
  // page qui indique la bonne adresse. Logique applicative, isolée (§102).
  if (origineBloquee) return <SecureOriginPage origin={origineBloquee} />

  return (
    <MetaCtx.Provider value={meta}>
      <MeCtx.Provider value={me}>
      <MeReloadCtx.Provider value={reloadMe}>
        <ToastProvider>
        <ConfirmProvider>
        <EditingProvider>
        <MenuContextuelProvider>
          <div className="lc-bg" aria-hidden="true">
            <div className="lc-bg__wash-a" />
            <div className="lc-bg__wash-b" />
            <div className="lc-bg__spot" />
          </div>

          <div className="lc-shell2">
            <Sidebar meta={meta} me={me} mobileOpen={mobileNav} onMobileClose={() => setMobileNav(false)} />

            <div className="lc-main">
              <header className="lc-header">
                <button className="lc-burger" onClick={() => setMobileNav(true)} aria-label="Ouvrir la navigation" aria-expanded={mobileNav} aria-controls="lc-nav-tiroir">
                  <Menu size={20} />
                </button>

                {/* Le champ n'est plus un input inerte : c'est LE déclencheur
                    de la Command Palette. */}
                <button className="lc-search lc-search--trigger" onClick={() => setPaletteOpen(true)} aria-label="Recherche globale (Ctrl+K)">
                  <Search size={18} className="lc-search__icon" />
                  <span className="lc-search__ph">Rechercher…</span>
                  <span className="lc-search__kbd">⌘K</span>
                </button>

                <span className="lc-header__spacer" />
                {/*
                  LA CLOCHE A ÉTÉ RETIRÉE — un seul point d'entrée.

                  Deux endroits annonçaient « il y a quelque chose à voir » :
                  cette cloche et l'entrée « Alertes » du menu latéral, chacune
                  avec son propre badge, comptant des choses différentes. Rien
                  n'indiquait laquelle faisait foi.

                  Le point d'entrée canonique est le menu latéral, et il mène
                  désormais au CENTRE D'ACTIVITÉ (/activite) : alertes encore
                  vraies épinglées en haut, messages reçus dans le flux. Une
                  seule destination, un seul badge.
                */}
                {me ? <BoutonFavori adresse={adresse} /> : null}
{/* « À voir » (04/11/2026) remplace le compteur de services, le badge
                    « Mode sûr » et l'état du menu : ce qui demande une action. */}
                <AVoir />
              </header>

              <div className="lc-scroll" ref={scrollRef}>
                <ModuleOnglets adresse={adresse} peut={peut} />
                <div className="lc-route" key={path}>
                  {props.children}
                </div>
              </div>
            </div>
          </div>

          <CommandPalette
            open={paletteOpen}
            onClose={() => setPaletteOpen(false)}
            permissions={me?.permissions ?? []}
            authenticated={me !== null}
          />

          {/* Expiration de session anticipée (vérité serveur, prolongation
              réelle, synchronisation multi-onglets). */}
          <SessionExpiryGuard authenticated={me !== null} />

          {/* Le sceau de finalisation : hors de `.lc-route`, il traverse le
              changement de page qu'il accompagne (sceau-bus.ts). */}
          <SceauHost scrollRef={scrollRef} />

          {/* « Actualiser » (Sonde) : la réponse en carte, en bas à droite, quelle que soit la page. */}
          {me ? <ActualisationsHote /> : null}
        </MenuContextuelProvider>
        </EditingProvider>
        </ConfirmProvider>
        </ToastProvider>
      </MeReloadCtx.Provider>
      </MeCtx.Provider>
    </MetaCtx.Provider>
  )
}
